const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

type RewardfulReady = (method: 'ready', callback: () => void) => void

declare global {
  interface Window {
    Rewardful?: {
      referral?: unknown
    }
    rewardful?: RewardfulReady
  }
}

function readRewardfulReferral(): string | undefined {
  try {
    const referral = window.Rewardful?.referral
    return typeof referral === 'string' && UUID_PATTERN.test(referral)
      ? referral
      : undefined
  } catch {
    return undefined
  }
}

/**
 * Resolve the current Rewardful referral without making Checkout depend on
 * the third-party script. The bootstrap queues `ready`; the timeout preserves
 * checkout availability when the script is delayed or blocked.
 */
export async function resolveRewardfulReferral(
  timeoutMs = 300,
): Promise<string | undefined> {
  if (typeof window === 'undefined') return undefined

  const current = readRewardfulReferral()
  if (current) return current
  let ready: RewardfulReady | undefined
  try {
    ready = window.rewardful
  } catch {
    return undefined
  }
  if (typeof ready !== 'function') return undefined

  return new Promise(resolve => {
    let settled = false

    const finish = () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(readRewardfulReferral())
    }

    const timer = setTimeout(finish, Math.max(0, timeoutMs))
    try {
      ready('ready', finish)
    } catch {
      finish()
    }
  })
}
